/**
 * Política de privacidade da app (e da extensão). Texto legal: qualquer alteração deve ser
 * revista por uma pessoa e manter as duas línguas com o mesmo conteúdo.
 */
import type { Locale } from '@nolens/i18n';
import { LEGAL_ADDRESS, LEGAL_NAME, LEGAL_TAX_ID } from '../lib/contact';

export const PRIVACY_EMAIL = 'privacidade@nolens.app';

/** Texto legal: título, data e secções (parágrafo e/ou lista). Também para os termos. */
export interface LegalText {
  title: string;
  updated: string;
  sections: { h: string; p?: string; li?: string[] }[];
}

const en: LegalText = {
  title: 'Privacy policy',
  updated: 'Nolens Guides, by rdoria.com. Last updated: October 26, 2026.',
  sections: [
    {
      h: 'Who is responsible',
      p: `The controller of the data described here is ${LEGAL_NAME}, address ${LEGAL_ADDRESS}, tax number ${LEGAL_TAX_ID}. For anything about your data, write to ${PRIVACY_EMAIL}.`,
    },
    {
      h: 'What we store',
      li: [
        'Your email, your account ID (Google or a one-time email code) and your language preference.',
        'The guides in your account: titles, steps, notes and screenshots.',
        'The teams you belong to and your role in each.',
      ],
    },
    {
      h: 'The Chrome extension',
      p: 'The extension only works while connected to your account and only reads pages during a recording you start. While you record, nothing is sent: steps and screenshots stay on your computer, encrypted, until you press “Finish”. They then go to your account and the local copy is deleted. If sending fails, the recording stays on your computer until you send it again or discard it.',
    },
    {
      h: 'What never leaves your computer',
      p: 'Before sending, the extension automatically blurs the personal data it detects in screenshots (email addresses, phone numbers, tax IDs, IBANs, card numbers), removes parameters from page addresses and never reads what is typed in sensitive fields (passwords, cards, codes). Banks, personal email, HR platforms and password managers are protected and are not recorded.',
    },
    {
      h: 'Where data is kept',
      p: 'In the European Union: database and accounts on Supabase (Frankfurt) and screenshots on Cloudflare R2 under EU jurisdiction. We don’t sell data or use it for advertising.',
    },
    {
      h: 'Access by our team',
      p: 'For technical reasons, the people who run the service can access stored data, including guides and screenshots. We only do so when necessary: to give support you ask for (and only to what that support needs), to investigate a security problem or abuse of the service, or when the law requires it. Access is limited to the people who run the service, with two-factor authentication, and we never use guide content for anything else, such as advertising or training AI models.',
    },
    {
      h: 'AI suggestions',
      p: 'Only when you press “Suggest texts”: the text of the steps (type, element name, page title, domain and notes) is sent to Anthropic to propose titles and a description. Screenshots, full addresses and anything typed in fields are never sent. Under Anthropic’s commercial terms, this content is not used to train models.',
    },
    {
      h: 'Payments',
      p: 'Paid plans are bought through Paddle.com, our reseller and Merchant of Record. Paddle collects the payment details, billing address and tax information, under its own privacy policy; we never see or store card numbers. We only receive what we need to manage the plan: the email, the plan, the subscription status and the country.',
    },
    {
      h: 'Early access sign-ups',
      p: 'What you fill in on the early access page is kept in the database (EU) and sent by email to the team, through Resend, so we can contact you about the test. During the test, the problems you report (text and the images or PDF files you attach) are kept in the EU (database and Cloudflare R2) and only the team sees them. We use it for nothing else, and delete it on request.',
    },
    {
      h: 'Error reports',
      p: 'When something fails in the app or the extension, the error message and where it happened are sent to our servers so we can fix it. Before sending, email addresses, long numbers, codes and address parameters are removed; screenshots and guide content are never sent.',
    },
    {
      h: 'Views',
      p: 'We count how many times each guide is opened, per day and per source, without recording who read it.',
    },
    {
      h: 'Sharing',
      p: 'In the app, a guide is only visible to members of the space it’s in (see also “Access by our team”). If you turn on link sharing, anyone with the address can read it until you turn it off.',
    },
    {
      h: 'Companies and teams',
      p: 'When you use Nolens Guides for a company or another organization, the personal data that appears in guides is processed by us on that organization’s behalf, as its processor, under the terms of use (section “Personal data processed for organizations”).',
    },
    {
      h: 'Why we use it, and on what basis',
      li: [
        'To provide the service you signed up for (account, guides, teams, payments, AI suggestions when you ask for them): performance of the contract.',
        'Security, error reports, view counts and aggregated usage totals: our legitimate interest in keeping the service safe and working, with the minimum data.',
        'Early access sign-ups and the problems reported during the test: your request to take part, which you can withdraw at any time.',
        'Invoices and tax records: kept by Paddle, under its legal obligations.',
      ],
    },
    {
      h: 'Providers and transfers',
      li: [
        'Supabase: database and sign-in (Frankfurt, EU).',
        'Cloudflare: the app, the API and screenshot storage (R2 under EU jurisdiction); requests go through its global network.',
        'Anthropic: AI suggestions, only the text described above, only when you ask.',
        'Resend: sending emails (sign-in codes, team invitations and notices).',
        'Paddle.com: payments, as an independent controller under its own privacy policy (United Kingdom).',
        'Google: only if you sign in with Google, under its own privacy policy.',
        'Some of these providers are based in the United States. Where data leaves the EU, the transfer is covered by the European Commission’s standard contractual clauses or by the EU-US Data Privacy Framework, depending on the provider; the United Kingdom has an EU adequacy decision.',
      ],
    },
    {
      h: 'How long we keep it',
      li: [
        'Account, guides and teams: while the account exists. When you delete them they are removed at once, and leave our backups in the following cycle (up to 30 days).',
        `Free accounts with no activity for 12 months may be deleted after an email notice at least 30 days in advance.`,
        'The email of someone invited to a team: in the invitation while it is valid (14 days); expired invitations are deleted when the team manages invitations again, and all of them with the team.',
        'Server logs and error reports: up to 7 days.',
        'Early access sign-ups and reported problems: until the end of the test, or earlier if you ask.',
        'View counts: per day and per source, with no identity, while the guide exists.',
      ],
    },
    {
      h: 'Stored in your browser',
      p: 'The app keeps your session and a few preferences in your browser (local storage), and the extension keeps recordings in progress on your computer, encrypted, until they are sent. We don’t use advertising or analytics cookies. At checkout, the Paddle payment window may use its own cookies, under Paddle’s policy.',
    },
    {
      h: 'Your rights',
      p: `You can delete guides, teams and your account at any time (account deletion is on the Account page) and export your guides. You also have the right to access, correct, delete, restrict and object to the processing of your data, and to receive it in a portable format: write to ${PRIVACY_EMAIL}. We answer within one month. You may also complain to the Portuguese data protection authority (CNPD, www.cnpd.pt).`,
    },
  ],
};

const ptPT: LegalText = {
  title: 'Política de privacidade',
  updated: 'Nolens Guides, por rdoria.com. Última atualização: 26 de outubro de 2026.',
  sections: [
    {
      h: 'Quem é o responsável',
      p: `O responsável pelo tratamento dos dados descritos aqui é ${LEGAL_NAME}, com morada em ${LEGAL_ADDRESS}, NIF ${LEGAL_TAX_ID}. Para tudo o que diga respeito aos seus dados, escreva para ${PRIVACY_EMAIL}.`,
    },
    {
      h: 'O que guardamos',
      li: [
        'O seu email, o identificador da conta (Google ou código por email) e a língua que escolheu.',
        'Os guias da sua conta: títulos, passos, notas e capturas de ecrã.',
        'As equipas a que pertence e o seu papel em cada uma.',
      ],
    },
    {
      h: 'A extensão do Chrome',
      p: 'A extensão só funciona ligada à sua conta e só lê as páginas durante uma gravação iniciada por si. Enquanto grava, nada é enviado: os passos e as capturas ficam no seu computador, cifrados, até carregar em «Concluir». Seguem então para a sua conta e a cópia no computador é apagada. Se o envio falhar, a gravação fica no computador até a enviar outra vez ou a apagar.',
    },
    {
      h: 'O que nunca sai do seu computador',
      p: 'Antes de enviar, a extensão tapa automaticamente nas capturas os dados pessoais que deteta (emails, telefones, NIF, IBAN, números de cartão), retira dos endereços os parâmetros e nunca lê o que se escreve em campos sensíveis (palavras-passe, cartões, códigos). Bancos, email pessoal, plataformas de RH e gestores de palavras-passe estão protegidos e não são gravados.',
    },
    {
      h: 'Onde ficam os dados',
      p: 'Na União Europeia: base de dados e contas no Supabase (Frankfurt) e capturas no Cloudflare R2 com jurisdição UE. Não vendemos dados nem os usamos para publicidade.',
    },
    {
      h: 'Acesso da nossa equipa',
      p: 'Por razões técnicas, quem gere o serviço consegue aceder aos dados guardados, incluindo os guias e as capturas. Só o fazemos quando é necessário: para dar o apoio que nos pede (e só ao que esse apoio exigir), para investigar um problema de segurança ou um abuso do serviço, ou quando a lei o exige. O acesso está limitado a quem gere o serviço, com autenticação de dois fatores, e nunca usamos o conteúdo dos guias para outros fins, como publicidade ou treino de modelos de IA.',
    },
    {
      h: 'Sugestões por IA',
      p: 'Só quando carrega em «Sugerir textos»: o texto dos passos (tipo, nome do elemento, título da página, domínio e notas) é enviado à Anthropic para propor títulos e uma descrição. Nunca seguem capturas, endereços completos nem o que foi escrito em campos. Pelos termos comerciais da Anthropic, esse conteúdo não é usado para treinar modelos.',
    },
    {
      h: 'Pagamentos',
      p: 'Os planos pagos compram-se através do Paddle.com, o nosso revendedor e comerciante registado (Merchant of Record). O Paddle recolhe os dados de pagamento, a morada de faturação e os dados fiscais, ao abrigo da sua própria política de privacidade; nunca vemos nem guardamos números de cartão. Só recebemos o necessário para gerir o plano: o email, o plano, o estado da subscrição e o país.',
    },
    {
      h: 'Inscrições para testar',
      p: 'O que preenche na página de acesso antecipado fica na base de dados (UE) e é enviado por email à equipa, através do Resend, para o podermos contactar sobre o teste. Durante o teste, as anomalias que reporta (o texto e as imagens ou ficheiros PDF que juntar) ficam na UE (base de dados e Cloudflare R2) e só a equipa as vê. Não as usamos para mais nada e apagamo-las a pedido.',
    },
    {
      h: 'Registo de erros',
      p: 'Quando algo falha na app ou na extensão, a mensagem do erro e o sítio onde aconteceu são enviados para os nossos servidores, para o podermos corrigir. Antes do envio, são retirados emails, números longos, códigos e parâmetros dos endereços; nunca seguem capturas nem o conteúdo dos guias.',
    },
    {
      h: 'Leituras',
      p: 'Contamos quantas vezes cada guia é aberto, por dia e por origem, sem registar quem o leu.',
    },
    {
      h: 'Partilha',
      p: 'Na app, um guia só é visto pelos membros do espaço onde está (veja também «Acesso da nossa equipa»). Se ligar a partilha por ligação, qualquer pessoa com o endereço pode lê-lo até a desligar.',
    },
    {
      h: 'Empresas e equipas',
      p: 'Quando usa o Nolens Guides para uma empresa ou outra organização, os dados pessoais que aparecem nos guias são tratados por nós em nome dessa organização, como subcontratante, segundo os termos de utilização (secção «Dados pessoais tratados para organizações»).',
    },
    {
      h: 'Para que usamos os dados e com que fundamento',
      li: [
        'Para prestar o serviço que pediu (conta, guias, equipas, pagamentos e sugestões por IA quando as pede): execução do contrato.',
        'Segurança, registo de erros, contagem de leituras e totais de utilização agregados: o nosso interesse legítimo em manter o serviço seguro e a funcionar, com o mínimo de dados.',
        'Inscrições para testar e anomalias reportadas no teste: o seu pedido para participar, que pode retirar a qualquer momento.',
        'Faturas e registos fiscais: guardados pelo Paddle, ao abrigo das obrigações legais dele.',
      ],
    },
    {
      h: 'Fornecedores e transferências',
      li: [
        'Supabase: base de dados e entrada na conta (Frankfurt, UE).',
        'Cloudflare: a app, a API e o armazenamento das capturas (R2 com jurisdição UE); os pedidos passam pela rede global dela.',
        'Anthropic: sugestões por IA, só o texto descrito acima e só quando as pede.',
        'Resend: envio de emails (códigos de entrada, convites para equipas e avisos).',
        'Paddle.com: pagamentos, como responsável independente, ao abrigo da política de privacidade dele (Reino Unido).',
        'Google: só se entrar com a conta Google, ao abrigo da política de privacidade dele.',
        'Alguns destes fornecedores são dos Estados Unidos. Quando os dados saem da UE, a transferência faz-se com as cláusulas contratuais-tipo da Comissão Europeia ou ao abrigo do Quadro de Privacidade de Dados UE-EUA, conforme o fornecedor; o Reino Unido tem uma decisão de adequação da UE.',
      ],
    },
    {
      h: 'Quanto tempo guardamos',
      li: [
        'Conta, guias e equipas: enquanto a conta existir. Quando os apaga, saem logo, e desaparecem das cópias de segurança no ciclo seguinte (até 30 dias).',
        `As contas do plano Grátis sem atividade durante 12 meses podem ser apagadas, depois de um aviso por email com pelo menos 30 dias de antecedência.`,
        'O email de quem é convidado para uma equipa: no convite, enquanto for válido (14 dias); os convites expirados são apagados quando a equipa volta a gerir convites, e todos com a equipa.',
        'Registos do servidor e de erros: até 7 dias.',
        'Inscrições para testar e anomalias reportadas: até ao fim do teste, ou antes, se o pedir.',
        'Contagem de leituras: por dia e por origem, sem identificação, enquanto o guia existir.',
      ],
    },
    {
      h: 'Guardado no seu browser',
      p: 'A app guarda a sua sessão e algumas preferências no browser (armazenamento local), e a extensão guarda as gravações em curso no seu computador, cifradas, até serem enviadas. Não usamos cookies de publicidade nem de estatística. No pagamento, a janela do Paddle pode usar cookies próprios, ao abrigo da política do Paddle.',
    },
    {
      h: 'Os seus direitos',
      p: `Pode apagar guias, equipas e a sua conta a qualquer momento (apagar a conta está na página «Conta») e exportar os seus guias. Tem ainda direito de acesso, retificação, apagamento, limitação e oposição ao tratamento dos seus dados, e de os receber num formato portável: escreva para ${PRIVACY_EMAIL}. Respondemos no prazo de um mês. Pode também apresentar queixa à Comissão Nacional de Proteção de Dados (CNPD, www.cnpd.pt).`,
    },
  ],
};

const PRIVACY: Record<Locale, LegalText> = { en, 'pt-PT': ptPT };
export const privacyText = (l: Locale): LegalText => PRIVACY[l];
